/* Synthetic watch detail for the sample runners, in the same shape a real import produces, so the
 * samples show every page a watch wearer would see. Deterministic (seeded) and simple, plausible
 * physiology: cadence rises a little with speed (most extra speed comes from longer strides),
 * climbs are slower, heart rate rises a little in the heat, weather follows the seasons.
 * Every number it makes is labelled as a synthetic runner in the report. */

const SPEED_MIN = 1.8, SPEED_STEP = 0.1, SPEED_BINS = 33;
const GRADE_CENTRES = [-10, -6.5, -4, -2, 0, 2, 4, 6.5, 10];
const NOMINALS = [5000, 10000, 15000, 21097.5, 42195];

function rng(seed) {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
const r1 = (x) => Math.round(x * 10) / 10;
const median = (xs) => { const s = [...xs].sort((a, b) => a - b), m = s.length >> 1; return s.length ? (s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2) : null; };

/* cfg: { seed, walkShare (share of time walking, 0 = continuous runner), runSegS, vo2: [start, end] } */
export function addWatchDetail(runs, cfg, yearStartKey = '2026-01-01') {
  const rnd = rng(cfg.seed), noise = (s) => (rnd() * 2 - 1) * s;
  const y0 = Date.parse(yearStartKey), first = runs.length ? Date.parse(runs[0].date) : y0, last = runs.length ? Date.parse(runs[runs.length - 1].date) : y0;
  for (const r of runs) {
    if (r.indoor || !r.avgHr) continue;
    const t = Date.parse(r.date), doy = (t - y0) / 864e5, prog = (t - first) / Math.max(1, last - first);

    // weather: northern-hemisphere seasons, about 2°C in January to 20°C in July
    const temp = r1(11 - 9 * Math.cos((2 * Math.PI * (doy - 15)) / 365) + noise(3));
    r.wx = { temp, dew: r1(temp - 5 - rnd() * 4), rh: Math.round(60 + rnd() * 25), feels: r1(temp + noise(1)) };
    // the same effort costs a few more beats when it's warm
    r.avgHr = Math.round(r.avgHr * (1 + 0.004 * (temp - 12)));
    if (r.maxHr) r.maxHr = Math.max(r.maxHr, r.avgHr + 4);

    // run/walk split (walking at ~1.4 m/s)
    const walkSecs = r.movingS * (r.isRace ? cfg.walkShare * 0.6 : cfg.walkShare) * (cfg.walkShare ? 1 + noise(0.25) : 0);
    const walkDist = walkSecs * 1.4, runSecs = r.movingS - walkSecs, runDist = r.distM - walkDist, runV = runDist / runSecs;
    const spm0 = 148 + 11 * runV + noise(2);

    // cadence and stride by speed: cadence ∝ speed^0.25
    const speedBins = Array.from({ length: SPEED_BINS }, () => [0, 0, 0, 0, 0, 0, 0]);
    const ws = speedBins.map((_, i) => { const v = SPEED_MIN + (i + 0.5) * SPEED_STEP; return Math.exp(-((v - runV) ** 2) / (2 * 0.22 ** 2)); });
    const wsum = ws.reduce((a, b) => a + b, 0);
    ws.forEach((w, i) => {
      const secs = (runSecs * w) / wsum; if (secs < 1) return;
      const v = SPEED_MIN + (i + 0.5) * SPEED_STEP, spm = spm0 * (v / runV) ** 0.25, hr = r.avgHr * (v / runV) ** 0.6, gct = 270 - 14 * v;
      speedBins[i] = [secs, spm * secs, (v / (spm / 60)) * secs, gct * secs, hr * secs, secs, secs].map(r1);
    });

    // grade: hillier routes spread time into steeper bins; climbs slower, gentle descents faster
    const hilly = Math.max(0.8, (r.elevGainM / Math.max(1, r.distM)) * 100 * 2.5);
    const gw = GRADE_CENTRES.map((g) => Math.exp(-(g * g) / (2 * hilly * hilly))), gsum = gw.reduce((a, b) => a + b, 0);
    const walkW = GRADE_CENTRES.map((g) => Math.exp(-(g * g) / (2 * hilly * hilly)) * (g > 1 ? 1 + g * 0.35 : g < -1 ? 0.6 : 1)), wwsum = walkW.reduce((a, b) => a + b, 0);
    const gradeBins = GRADE_CENTRES.map((g, i) => {
      const rs = (runSecs * gw[i]) / gsum, wk = walkSecs ? (walkSecs * walkW[i]) / wwsum : 0;
      const paceF = g > 0 ? 1 + 0.035 * g : g > -6 ? 1 + 0.012 * g : 1 + 0.01 * (-g - 6);
      const rd = (rs * runV) / paceF, hr = r.avgHr * (1 + 0.008 * g);
      return [rd + wk * 1.4, rs + wk, wk, hr * (rs + wk), rs + wk, rd, rs].map((x) => Math.round(x));
    });

    // walk breaks: one per run segment
    let breaks = null, runSegMed = null;
    if (walkSecs > 0) {
      const seg = cfg.runSegS * (1 + noise(0.15)), n = Math.max(1, Math.round(runSecs / seg));
      const list = Array.from({ length: n }, (_, k) => [Math.round(((k + 1) * r.distM) / (n + 1)), Math.round(Math.min(110, (walkSecs / n) * (1 + noise(0.3)))), Math.round(9 + rnd() * 8)]).filter((b) => b[1] >= 10);
      if (list.length) breaks = { n: list.length, medDur: median(list.map((b) => b[1])), medDrop: median(list.map((b) => b[2])), list };
      runSegMed = Math.round(seg);
      // Garmin-style totals: durations in ms, distances in cm
      r.walk = { run: Math.round(runSecs * 1000), walk: Math.round(walkSecs * 1000), stand: 0, runM: Math.round(runDist * 100), walkM: Math.round(walkDist * 100), walks: n };
    } else r.walk = { run: Math.round(runSecs * 1000), walk: 0, stand: 0, runM: Math.round(runDist * 100), walkM: 0, walks: 0 };

    r.lab = {
      beatsPerKm: r1((r.avgHr * (r.movingS / 60)) / (r.distM / 1000)),
      run: { secs: Math.round(runSecs), dist: Math.round(runDist), spm: r1(spm0), step: Math.round((runV / (spm0 / 60)) * 1000) / 1000, gct: r1(270 - 14 * runV), vr: r1(8 + noise(0.6)), power: null },
      speedBins, gradeBins,
      decoupling: r.movingS >= 3600 ? r1(2.5 + noise(2) + 0.12 * (temp - 12)) : null,
      walkDetect: 'watch', breaks, runSegMed,
    };
    // the watch's VO2max estimate drifts up with fitness
    r.vo2 = Math.round(cfg.vo2[0] + (cfg.vo2[1] - cfg.vo2[0]) * prog + noise(0.6));

    if (r.isRace) {
      r.raceNominal = NOMINALS.reduce((a, m) => (Math.abs(m - r.distM) < Math.abs(a - r.distM) ? m : a), NOMINALS[0]);
      r.spot = raceProfile(r, walkSecs, rnd);
    }
  }
  return runs;
}

/* Per-km splits, a 100 m trace (distance, time, heart rate, altitude) and walk breaks for race day. */
function raceProfile(r, walkSecs, rnd) {
  const noise = (s) => (rnd() * 2 - 1) * s;
  const n = Math.ceil(r.distM / 1000), base = r.movingS / (r.distM / 1000);
  const walkPerKm = walkSecs / n;
  let alt = 40 + rnd() * 80, t = 0;
  const km = [], trace = [], walks = [];
  for (let k = 1; k <= n; k++) {
    const m = Math.min(1000, r.distM - (k - 1) * 1000);
    const fade = 1 + 0.06 * ((k - 1) / n) ** 2; // slows a little late in the race
    const s = Math.round(base * fade * (1 + noise(0.015)) * (m / 1000));
    const hr = Math.round(r.avgHr * (0.95 + 0.08 * (k / n)) + noise(2));
    alt = Math.max(0, alt + noise(8));
    const wk = walkSecs ? Math.round(walkPerKm * (k > 3 ? 1.2 : 0.4)) : 0;
    for (let d = 0; d < m; d += 100) trace.push([Math.round((k - 1) * 1000 + d), Math.round(t + (s * d) / m), hr + Math.round(noise(2)), Math.round(alt + noise(2))]);
    if (wk >= 10) walks.push([(k - 1) * 1000 + 550, (k - 1) * 1000 + 600, 0]);
    t += s;
    km.push({ km: k, s, m, hr, alt: Math.round(alt), walkS: wk, ...(m < 1000 ? { partial: true } : {}) });
  }
  // scale so the profile adds up to the race's finish time
  const f = r.elapsedS / t;
  km.forEach((x) => (x.s = Math.round(x.s * f)));
  trace.forEach((p) => (p[1] = Math.round(p[1] * f)));
  return { km, trace, walks };
}
